import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useVoicePlanLogs } from '../api';
import type { VoicePlanLogRow } from '../types';
import { dashIfEmpty } from '../utils';

export function VoicePlanLogsPage() {
  const query = useVoicePlanLogs();
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<VoicePlanLogRow>[]>(
    () => [
      {
        accessorKey: 'oldPlanId',
        header: 'Old Plan',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            #{row.original.oldPlanId}
          </span>
        ),
      },
      {
        accessorKey: 'newPlanId',
        header: 'New Plan',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs">#{row.original.newPlanId}</span>
        ),
      },
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => dashIfEmpty(row.original.username),
      },
      {
        accessorKey: 'requestDate',
        header: 'Changed at',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.requestDate ?? ''),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Plan Audit"
        description="Every voice-plan change applied to users under your account, with the previous and new plan."
      />
      <DataTable<VoicePlanLogRow, unknown>
        tableId="reports:voice-plan-logs:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search by username or plan ID…"
        emptyTitle="No plan changes yet"
        emptyDescription="When a voice plan is updated for any user, the change shows up here."
        getRowId={(row, idx) => `${row.userId}-${row.requestDate ?? idx}`}
        exportFilename="voice-plan-logs.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
